import type { Messages } from "./messages.en";

/**
 * Thai. Two things drive the wording here rather than the English original.
 *
 * Thai counts with classifiers, so "5 words" is "คำ" after the number and never
 * a plural form, and every count sentence is written out rather than assembled.
 * Thai also has no spaces between words, so a sentence broken into fragments
 * and glued back together reads as a mistake; anything the English catalogue
 * built from pieces is one string here.
 */
export const th: Messages = {
  nav: {
    home: "หน้าแรก SprinkleDays",
    guides: "บทความ",
    questions: "คำถาม",
    allGuides: "บทความทั้งหมด",
    startCourse: "เริ่มเรียน",
    openToday: "เปิดใบงานของวันนี้",
    howItWorks: "วิธีใช้งาน",
    reading: "อ่าน",
    footer: "ท้ายหน้า",
    breadcrumb: "เส้นทางหน้า",
    language: "ภาษา",
    languageOf: (label: string) => `อ่านหน้านี้เป็น${label}`,
    footerBlurb:
      "SprinkleDays เปลี่ยนหลักสูตรให้เป็นใบงานพิมพ์ได้วันละแผ่น สองภาษาวางคู่กันโดยมีเส้นพับคั่นกลาง ไม่ต้องสมัครสมาชิก และไม่มีข้อมูลใดออกจากเครื่องของคุณ",
  },

  languages: {
    en: "อังกฤษ",
    nl: "ดัตช์",
    th: "ไทย",
    or: (names: readonly string[]) =>
      `${names.slice(0, -1).join(" ")} หรือ${names.at(-1)}`,
    to: (from: string, to: string) => `${from} เป็น ${to}`,
  },

  home: {
    headline: {
      lead: "เรียนภาษา",
      emphasis: "วันละไม่กี่คำ",
      tail: "",
    },
    lede: "วันละหนึ่งแผ่น สองภาษาวางคู่กัน พิมพ์ทั้งสัปดาห์ไว้บนโต๊ะอาหาร หรืออ่านใบงานของวันนั้นบนมือถือก็ได้ ถ้าเรียนไปพร้อมกับใครสักคน พับแผ่นตรงกลางแล้วผลัดกันถามได้เลย",
    learning: "ภาษาที่คุณกำลังเรียน",
    speak: "ภาษาที่คุณพูดได้แล้ว",
    wordsADay: "คำต่อวัน",
    sentences: "ประโยค",
    whoIsLearning: "ใครเป็นคนเรียน",
    justMe: "เรียนคนเดียว",
    twoOfUs: "เรียนสองคน",
    nameLeft: "ชื่อฝั่งซ้าย",
    nameRight: "ชื่อฝั่งขวา",
    namePlaceholderLeft: "เช่น ป๋า",
    namePlaceholderRight: "เช่น นก",
    namesHint:
      "ชื่อทั้งสองจะพิมพ์อยู่เหนือคอลัมน์ แต่ละคนจะได้รู้ว่าต้องพับฝั่งไหนเก็บไว้",
    start: "เริ่มที่วันที่ 1",
    save: "บันทึกแล้วเปิดวันนี้",
    fine: "ไม่ต้องสมัครสมาชิก ไม่ต้องใส่บัตร ไม่มีอะไรให้ยกเลิก ทุกอย่างอยู่ในเครื่องนี้",
    sampleTag: "วันละหนึ่งแผ่น A4",
    sampleTitle: "หนึ่งวันหน้าตาเป็นแบบนี้",
    sampleLede:
      "สองภาษาของคุณวางคู่กัน มีเส้นพับผ่ากลาง ใบงานจะเปลี่ยนตามการตั้งค่าด้านบนทันที",
    sampleNotes: [
      "พับตามเส้นเพื่อปิดฝั่งหนึ่งไว้แล้วลองทดสอบตัวเอง",
      "พิมพ์ทั้งสัปดาห์รวดเดียว หรือพิมพ์ทีละวันก็ได้",
      "บนหน้าจอ แตะที่คำไหนก็ได้เพื่อฟังเสียงอ่าน",
      "สแกนใบงานที่พิมพ์ไว้เพื่อเปิดวันนั้นแล้วกดว่าทำเสร็จ",
    ],
    howTitle: "สามนาทีที่โต๊ะอาหาร",
    steps: [
      {
        title: "เลือกสองภาษาของคุณ",
        body: "ภาษาที่กำลังเรียนกับภาษาที่พูดได้อยู่แล้ว มีไทย ดัตช์ และอังกฤษ จับคู่แบบไหนก็ได้",
      },
      {
        title: "พิมพ์ทั้งสัปดาห์",
        body: "เจ็ดแผ่น A4 วันละแผ่น สองภาษาวางคู่กัน หรือจะเปิดบนหน้าจอแล้วแตะฟังเสียงแต่ละคำก็ได้",
      },
      {
        title: "พับแล้วทดสอบ",
        body: "พับตามเส้นประให้ฝั่งหนึ่งหายไป แล้วลองตอบเอง ถ้าเรียนกับคนอื่นก็ผลัดกันถาม",
      },
    ],
    premiumTag: "พรีเมียม",
    premiumJoinedTitle: "คุณอยู่ในรายชื่อแล้ว",
    premiumJoinedLede:
      "ทุกสัปดาห์ของหลักสูตรเปิดให้คุณแล้วบนเครื่องนี้ เราส่งอีเมลไม่บ่อย และส่งเฉพาะเรื่องหลักสูตรเท่านั้น",
    premiumTitleStrong: "สมัครพรีเมียม",
    premiumTitleRest: "เพื่อเปิดครบทุกสัปดาห์",
    premiumLede:
      "หลักสูตรเดียว ไม่ว่าจะมีคนเรียนคนเดียวหรือห้าคน ใบงานทั้งสัปดาห์เปิดพร้อมกัน พิมพ์รวดเดียวหรืออ่านบนมือถือก็ได้",
    premiumAsk:
      "ทิ้งอีเมลไว้เพื่อเข้าร่วมรายชื่อรุ่นแรก แล้วทุกสัปดาห์ของหลักสูตรจะเปิดบนเครื่องนี้",
    premiumSubmit: "สมัครพรีเมียม",
    premiumFine:
      "เราส่งอีเมลของคุณ สองภาษาที่เลือก และจังหวะที่ตั้งไว้ ส่วนใบงาน ชื่อ และความคืบหน้าอยู่ในเครื่องนี้ทั้งหมด",
    readsTag: "ก่อนเริ่มเรียน",
    readsTitle: "อ่านห้านาทีคุ้มค่า",
    readsBody:
      "ควรตั้งวันละกี่คำ ทำไมการพับใบงานได้ผลกว่าการอ่านซ้ำ และเดือนแรกของภาษาไทยควรครอบคลุมอะไรบ้าง",
    readsInEnglish: "บทความเขียนเป็นภาษาอังกฤษ",
    footerBlurb:
      "เรียนวันละนิด ทำขึ้นเพื่อทุกคนที่ค่อย ๆ เก็บภาษาทีละไม่กี่คำ จะเรียนคนเดียวหรือเรียนกับคนที่บ้านก็ได้",
  },

  articles: {
    tag: "บทความ",
    title: "เรียนภาษาทีละไม่กี่คำ",
    minRead: (minutes: number) => `อ่าน ${minutes} นาที`,
    ctaTitle: "เริ่มที่วันที่ 1",
    ctaBody:
      "เลือกสองภาษาและจำนวนคำต่อวันที่เหมาะกับคุณ ใบงานแผ่นแรกพร้อมก่อนคุณอ่านประโยคนี้จบ และไม่ต้องสมัครสมาชิก มีคำถามก่อนไหม",
    ctaLink: "อ่านคำตอบ",
    categories: {
      Method: "วิธีเรียน",
      Thai: "ภาษาไทย",
      Dutch: "ภาษาดัตช์",
      English: "ภาษาอังกฤษ",
      Together: "เรียนด้วยกัน",
    },
  },

  faq: {
    tag: "คำถาม",
    title: "คำถามเกี่ยวกับ SprinkleDays",
    moreTitle: "คำตอบแบบยาว",
    moreBodyBefore: "คำถามด้านบนเป็นเรื่องของ SprinkleDays ส่วน",
    moreBodyLink: "บทความ",
    moreBodyAfter: "ตอบคำถามที่ยากกว่านั้นเกี่ยวกับการเรียนภาษา",
    ctaTitle: "ไม่เหลืออะไรให้ตัดสินใจแล้ว",
    ctaBody:
      "สองภาษา จำนวนคำต่อวัน แล้วก็วันที่ 1 ทุกอย่างอยู่ในเครื่องนี้ และใบงานแผ่นแรกพร้อมในเวลาประมาณหนึ่งนาที",
    ctaButton: "เริ่มที่วันที่ 1",
  },

  notFound: {
    title: "หน้านี้หายไปแล้ว",
    body: "เราไม่มีที่อยู่นี้ ส่วนอย่างอื่นยังอยู่ที่เดิม",
    cta: "ไปที่หน้าเริ่มต้น",
    asideBefore: "มาจากการค้นหาใช่ไหม",
    asideGuides: "บทความ",
    asideAnd: "และ",
    asideQuestions: "หน้าคำถาม",
    asideAfter: "ยังอยู่ที่เดิม",
  },

  app: {
    mainNav: "เมนูหลัก",
    today: "วันนี้",
    thisWeek: "สัปดาห์นี้",
    settings: "ตั้งค่า",
    booting: "กำลังเปิดใบงานของคุณ…",
    loading: "กำลังโหลด…",
    gettingWeekReady: "กำลังเตรียมสัปดาห์นี้…",
    retry: "ลองอีกครั้ง",
    day: (day: number) => `วันที่ ${day}`,
    dayOf: (day: number, total: number) => `${day} / ${total}`,
    previousDay: "วันก่อนหน้า",
    nextDay: "วันถัดไป",
    backToWeek: "กลับไปสัปดาห์นี้",
    notReleasedTitle: "ยังไม่ถึงเวลา และนั่นคือความตั้งใจ",
    notReleasedBody: (day: number, week: number, date: string) =>
      `ใบงานมาทีละสัปดาห์ วันที่ ${day} จะมาพร้อมสัปดาห์ที่ ${week} ในวันที่ ${date}`,
    finished:
      "คุณเรียนจบหลักสูตรแล้ว ใบงานทุกแผ่นยังเปิดอยู่ให้ทวนอีกรอบได้เสมอ",
    weekTag: (words: number, days: number) =>
      `วันละ ${words} คำ · ${days} วัน`,
    week: (week: number) => `สัปดาห์ที่ ${week}`,
    weekOf: (week: number, total: number) => `${week} / ${total}`,
    daysDone: (done: number, total: number) =>
      `ทำแล้ว ${done} จาก ${total} วัน`,
    earlierWeek: "สัปดาห์ก่อนหน้า",
    laterWeek: "สัปดาห์ถัดไป",
    dayLocked: (day: number, weekday: string) =>
      `วันที่ ${day} ${weekday} ยังล็อกอยู่`,
    dayLabel: (day: number, weekday: string, done: boolean) =>
      `วันที่ ${day} ${weekday}${done ? " ทำแล้ว" : ""}`,
    arriveOn: (date: string) => `ใบงานชุดนี้จะมาในวันที่ ${date}`,
    weekWaitingTitle: (week: number) => `สัปดาห์ที่ ${week} รออยู่`,
    weekWaitingBody: (day: number) =>
      `สองภาษาวางคู่กัน ทั้งสัปดาห์พิมพ์รวดเดียวได้ เปิดวันที่ ${day} เพื่อไปต่อ`,
    openDay: (day: number) => `เปิดวันที่ ${day}`,
    printWeek: "พิมพ์สัปดาห์นี้",
    sheetsNote: (sheets: number) =>
      `${sheets} แผ่น แผ่นละหนึ่งหน้า A4 พับแต่ละแผ่นตรงกลางแล้วกดว่าทำเสร็จเมื่อจบวัน`,
    sheetsPending: "กำลังเตรียมใบงาน…",

    settingsTitle: "แก้อะไรก็ได้ ความคืบหน้ายังอยู่",
    settingsLede: "ทุกอย่างบันทึกอัตโนมัติ และอยู่ในเครื่องนี้",
    languagesPanel: "ภาษา",
    leftColumn: "คอลัมน์ซ้าย",
    rightColumn: "คอลัมน์ขวา",
    nameAboveLeft: "ชื่อเหนือคอลัมน์ซ้าย",
    nameAboveRight: "ชื่อเหนือคอลัมน์ขวา",
    namePlaceholderLeft: "ใส่หรือไม่ก็ได้ เช่น ป๋า",
    namePlaceholderRight: "ใส่หรือไม่ก็ได้ เช่น นก",
    showPronunciation: "แสดงคำอ่านภาษาไทย",
    pacePanel: "วันละเท่าไร",
    sentencesADay: "ประโยคต่อวัน",
    paceNote:
      "ยิ่งเรียนวันละหลายคำ จำนวนวันจนจบหลักสูตรก็ยิ่งน้อยลง ตัวคำศัพท์ยังเรียงลำดับเหมือนเดิม",
    coursePanel: "หลักสูตรของคุณ",
    started: "เริ่มเมื่อ",
    timezone: "เขตเวลา",
    access: "สิทธิ์เข้าถึง",
    fullCourse: "ครบทั้งหลักสูตร",
    restartPanel: "เริ่มใหม่ที่วันที่ 1",
    restartBody:
      "การเริ่มใหม่จะเก็บภาษาและจำนวนต่อวันไว้เหมือนเดิม แต่จะล้างทุกวันที่คุณกดว่าทำเสร็จ",
    restartConfirm: "ล้างความคืบหน้าทั้งหมดไหม",
    restartYes: "ใช่ เริ่มใหม่",
    restartNo: "เก็บไว้",
    restart: "เริ่มใหม่",

    swapSides: "สลับสองฝั่ง",
    leftSide: "ฝั่งซ้ายของใบงาน",
    rightSide: "ฝั่งขวาของใบงาน",
    decrease: (unit: string) => `ลด 1 ${unit}`,
    increase: (unit: string) => `เพิ่ม 1 ${unit}`,
    unitWord: "คำ",
    unitSentence: "ประโยค",

    sheetFailed: "โหลดใบงานไม่สำเร็จ",
    printingSheet: "กำลังจัดใบงานของวันนี้…",
    markDone: "กดว่าทำเสร็จแล้ว",
    notDoneYet: "ยังไม่เสร็จ",
    print: "พิมพ์",
    pacePill: (words: number, sentences: number) =>
      `${words} คำ · ${sentences} ประโยค`,
    controlsNote:
      "การเปลี่ยนจำนวนคำต่อวันเปลี่ยนแค่จังหวะ ไม่ได้เปลี่ยนเนื้อหา คำชุดเดิมแค่ถูกกระจายไปในวันที่มากขึ้นหรือน้อยลง",

    email: "อีเมลของคุณ",
    emailPlaceholder: "you@example.com",
    emailInvalid: "อันนี้ดูไม่เหมือนอีเมล",
    sendFailed: "มีบางอย่างผิดพลาด กรุณาลองใหม่อีกครั้ง",
    sending: "สักครู่…",
  },

  seo: {
    homeTitle: "SprinkleDays | เรียนภาษาวันละไม่กี่คำ",
    homeDescription:
      "เรียนภาษาวันละไม่กี่คำ ในจังหวะที่คุณกำหนดเอง ใบงานพิมพ์ได้หนึ่งแผ่นที่มีสองภาษาวางคู่กัน จะเรียนคนเดียวหรือผลัดกันถามก็ได้",
    articlesTitle: "บทความเรื่องการเรียนภาษาวันละไม่กี่คำ | SprinkleDays",
    articlesDescription:
      "บทความใช้งานได้จริงเรื่องการฝึกภาษาทุกวัน ตั้งแต่การเลือกจังหวะที่ทำได้ต่อเนื่อง การทดสอบตัวเองบนกระดาษ ไปจนถึงการเริ่มภาษาไทยจากศูนย์",
    faqTitle: "คำถามเกี่ยวกับ SprinkleDays | SprinkleDays",
    faqDescription:
      "SprinkleDays ราคาเท่าไร มีภาษาอะไรบ้าง ใบงานแบบพับใช้ยังไง และข้อมูลไหนออกหรือไม่ออกจากเครื่องของคุณ",
    homeLabel: "SprinkleDays: เรียนภาษาวันละไม่กี่คำ",
    articlesLabel: "บทความทั้งหมดเรื่องการฝึกภาษาทุกวัน",
    faqLabel: "คำถามเกี่ยวกับ SprinkleDays",
  },
};
